import React from "react";
import moment from "moment";
import "moment-timezone";
import "moment/locale/th";

import useViewModel from "./ViewModel";

const PublicDashboardView: React.FC = () => {
  const {
    isShowWaiting,
    visibleQueues,
    totalPendingQueues,
    queuePageCount,
    pageStart,
    pageEnd,
    calledQueue,
    isVoiceSupported,
    isVoiceMuted,
    isVoiceSpeaking,
    voiceErrorText,
    speakingAnnouncementText,
    onToggleVoiceMute,
    onTestVoiceCall,
    updatedAt,
    advertisementEnabled,
    advertisementItems,
    currentAdvertisement,
    currentAdvertisementIndex,
    onAdvanceAdvertisement,
    onSelectAdvertisement,
  } = useViewModel();

  const getCurrentTimeText = () => {
    return moment()
      .tz("Asia/Bangkok")
      .locale("th")
      .add(543, "year")
      .format("DD MMMM YYYY HH:mm:ss");
  };

  const [currentTimeText, setCurrentTimeText] =
    React.useState<string>(getCurrentTimeText());

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTimeText(getCurrentTimeText());
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return (
    <React.Fragment>
      <div className="min-h-screen bg-slate-300 p-3 lg:h-screen lg:overflow-hidden lg:p-4">
        {isShowWaiting && (
          <div className={"h-full flex"}>
            <div className="bg-white rounded-lg px-8 py-5 flex flex-col justify-center items-center flex-1">
              <div className="text-center font-bold text-3xl">
                Loading queue...
              </div>
            </div>
          </div>
        )}

        {!isShowWaiting && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:h-full lg:min-h-0">
            <div className="lg:col-span-5 lg:min-h-0">
              <div className="bg-white rounded-lg shadow-lg p-4 lg:p-5 flex flex-col overflow-hidden lg:h-full lg:min-h-0">
                <div className="bg-slate-50 rounded-lg border border-slate-200 px-3 py-3 flex items-center gap-3">
                  <img
                    src="/ws-logo.png"
                    alt="Wungsun Clinic Logo"
                    className="w-12 h-12 rounded-full object-cover border border-slate-200"
                  />
                  <div className="min-w-0">
                    <div className="text-slate-700 font-bold text-base leading-tight">
                      คลินิคแพทย์วังสันต์
                    </div>
                    <div className="text-slate-500 text-xs">Beauty Zone</div>
                    <div className="text-slate-600 text-xs mt-1">
                      Current time {currentTimeText}
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={onToggleVoiceMute}
                      className="px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-700 text-white hover:bg-slate-800"
                    >
                      {isVoiceMuted ? "เปิดเสียงเรียก" : "ปิดเสียงเรียก"}
                    </button>
                    <button
                      type="button"
                      onClick={onTestVoiceCall}
                      disabled={!isVoiceSupported || isVoiceMuted}
                      className="px-3 py-1.5 rounded-md text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ทดสอบเสียงเรียกคิว
                    </button>
                  </div>
                  {!isVoiceSupported && (
                    <div className="text-xs text-center text-amber-600 mt-2">
                      This browser does not support voice announcement.
                    </div>
                  )}
                  {isVoiceSpeaking && (
                    <div className="text-xs text-center text-green-700 mt-2">
                      กำลังประกาศคิว: "{speakingAnnouncementText}"
                    </div>
                  )}
                  {voiceErrorText && (
                    <div className="text-xs text-center text-red-600 mt-2">
                      {voiceErrorText}
                    </div>
                  )}
                </div>

                <div className="text-gray-800 text-2xl text-center font-semibold mt-4">
                  คิวถัดไป
                </div>
                <div className="text-slate-500 text-sm text-center mt-2">
                  แสดงเฉพาะคิวรอเรียก
                </div>

                {visibleQueues.length === 0 && (
                  <div className="flex-1 flex items-center justify-center text-gray-400 text-2xl mt-4">
                    ไม่มีคิวรอเรียก
                  </div>
                )}

                {visibleQueues.length > 0 && (
                  <div className="mt-4 flex-1 flex flex-col justify-start overflow-hidden">
                    <div className="flex flex-col gap-2">
                      {visibleQueues.map((queue, index) => (
                        <div
                          key={queue.uuid}
                          className="bg-slate-50 rounded-xl border border-slate-200 px-3 py-2 flex items-center justify-between gap-3"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {/* <div className='w-8 h-8 rounded-full bg-slate-200 text-slate-600 text-base font-bold flex items-center justify-center shrink-0'>
                              {pageStart + index}
                            </div> */}
                            <div className="min-w-0">
                              <div className="text-slate-500 text-xs">
                                หมายเลขคิว
                              </div>
                              <div className="text-slate-700 text-4xl lg:text-5xl font-extrabold leading-none">
                                {queue.currentQueueNumber}
                              </div>
                            </div>
                          </div>
                          <div className="text-slate-600 text-sm lg:text-base font-semibold truncate max-w-[180px] text-right">
                            {queue.patientName || "ไม่ระบุชื่อ"}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-3 text-center text-slate-500 text-sm">
                      แสดง {pageStart}-{pageEnd} จาก {totalPendingQueues}
                      {queuePageCount > 1 ? " (เลื่อนอัตโนมัติ)" : ""}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-7 lg:min-h-0">
              <div className="flex h-full min-h-0 flex-col gap-4">
                <div
                  className={`bg-white rounded-lg shadow-lg px-6 py-6 lg:px-8 lg:py-7 flex flex-col justify-between ${
                    advertisementEnabled ? "lg:flex-[0_0_48%]" : "lg:flex-1"
                  }`}
                >
                  <div className="text-center">
                    <div className="text-orange-400 font-bold text-3xl">
                      คิวที่เรียก
                    </div>
                    <div className="mt-2 inline-flex px-3 py-1 rounded text-sm font-semibold bg-green-100 text-green-700">
                      กำลังตรวจ
                    </div>
                  </div>

                  <div className="text-center">
                    <h1 className="text-gray-700 font-extrabold text-8xl lg:text-9xl leading-none">
                      {calledQueue?.currentQueueNumber ?? "-"}
                    </h1>
                    <div className="text-slate-600 text-lg lg:text-2xl font-semibold mt-4">
                      {calledQueue?.patientName || "ไม่ระบุชื่อ"}
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="text-gray-400 text-base lg:text-lg">
                      เช็คอินเมื่อ
                    </div>
                    <div className="text-gray-700 text-xl lg:text-2xl font-semibold mt-2">
                      {calledQueue?.checkinAt
                        ? moment(calledQueue.checkinAt)
                            .tz("Asia/Bangkok")
                            .locale("th")
                            .add(543, "year")
                            .format("DD MMMM YYYY HH:mm")
                        : "-"}
                    </div>

                    <div className="text-gray-500 text-sm mt-4 lg:mt-5">
                      อัปเดตล่าสุด:{" "}
                      {updatedAt
                        ? moment(updatedAt)
                            .tz("Asia/Bangkok")
                            .locale("th")
                            .add(543, "year")
                            .format("DD MMMM YYYY HH:mm:ss")
                        : "-"}
                    </div>
                  </div>
                </div>

                {advertisementEnabled && (
                  <div className="bg-white rounded-lg shadow-lg px-2 py-2 lg:px-3 lg:py-3 flex min-h-0 flex-col overflow-hidden lg:flex-1">
                    <div className="relative aspect-video min-h-[220px] overflow-hidden rounded-xl border border-slate-200 bg-slate-100 lg:min-h-0">
                      {currentAdvertisement ? (
                        <>
                          {currentAdvertisement.type === "video" ? (
                            <video
                              key={currentAdvertisement.id}
                              src={currentAdvertisement.url}
                              className="absolute inset-0 h-full w-full object-cover"
                              autoPlay
                              muted
                              playsInline
                              controls={false}
                              loop={advertisementItems.length === 1}
                              onEnded={onAdvanceAdvertisement}
                            />
                          ) : (
                            <img
                              key={currentAdvertisement.id}
                              src={currentAdvertisement.url}
                              alt={currentAdvertisement.originalName}
                              className="absolute inset-0 h-full w-full object-cover"
                            />
                          )}

                          {advertisementItems.length > 1 && (
                            <div className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full bg-white/80 px-3 py-1 backdrop-blur-sm">
                              {advertisementItems.map((item, index) => (
                                <button
                                  key={item.id}
                                  type="button"
                                  onClick={() => onSelectAdvertisement(index)}
                                  className={`h-2.5 rounded-full transition-all ${
                                    index === currentAdvertisementIndex
                                      ? "w-8 bg-orange-400"
                                      : "w-2.5 bg-slate-300 hover:bg-slate-400"
                                  }`}
                                  aria-label={`Advertisement slide ${index + 1}`}
                                />
                              ))}
                            </div>
                          )}
                        </>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default PublicDashboardView;
